import { useEffect, useRef } from 'react';

const reasons = [
  {
    icon: 'ri-map-pin-2-line',
    title: 'Yerel ve hızlı hizmet',
    desc: 'Bölgenize odaklanan yerel bir servis olarak randevularınızı mümkün olan en kısa sürede planlıyoruz.',
  },
  {
    icon: 'ri-user-star-line',
    title: 'Deneyimli ustalar',
    desc: 'Sertifikalı ustalarla çalışıyoruz. Sorununuzu doğru tespit edip kalıcı çözüm üretmeyi hedefliyoruz.',
  },
  {
    icon: 'ri-price-tag-3-line',
    title: 'Uygun fiyat garantisi',
    desc: 'Parça ve işçilik maliyetlerini teklif öncesinde net paylaşır, sürpriz masrafların önüne geçeriz.',
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'Şeffaf fiyatlandırma',
    desc: 'İşe başlamadan önce yapılacak işlemleri ve maliyeti net belirtiyor, sürpriz ücret olmaz.',
  },
  {
    icon: 'ri-whatsapp-line',
    title: 'WhatsApp destek hattı',
    desc: 'Servis talebi ve ön bilgi için WhatsApp üzerinden kolayca iletişime geçebilirsiniz.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Garantili yedek parça',
    desc: 'Orijinal ve garantili parçalarla çalışır, kombinizin uzun ömürlü kullanılmasını sağlarız.',
  },
];

export default function WhyUsSection() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('opacity-100', 'translate-y-0');
          e.target.classList.remove('opacity-0', 'translate-y-6');
        }
      }),
      { threshold: 0.1 }
    );
    ref.current?.querySelectorAll('.anim').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="whyus" ref={ref} className="w-full py-16 md:py-24 bg-[#f0f7ff]">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="anim opacity-0 translate-y-6 transition-all duration-500 text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-3 tracking-tight">
            Neden Kombi Kurumsal Servis?
          </h2>
          <p className="text-sm text-slate-500 max-w-lg mx-auto">
            Güvenilir, şeffaf ve deneyimli teknik ekibimizle tüm kombi süreçlerinde yanınızdayız.
          </p>
        </div>

        {/* Content: image left + reasons right */}
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-12 mb-12">
          {/* Left image */}
          <div className="anim opacity-0 translate-y-6 transition-all duration-500 lg:w-[380px] flex-shrink-0">
            <div className="relative w-full h-[300px] md:h-[420px] bg-white rounded-2xl overflow-hidden border border-[#dbeafe]">
              <img
                src="/images/whyus-tech-01.jpg"
                alt="Deneyimli Kombi Ustası"
                className="w-full h-full object-cover object-center"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>

          {/* Right grid */}
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-5 content-start">
            {reasons.map((r, i) => (
              <div
                key={r.title}
                className="anim opacity-0 translate-y-6 transition-all duration-500 group flex gap-4 p-6 rounded-2xl border border-[#dbeafe] hover:border-primary/25 hover:shadow-lg hover:-translate-y-0.5 transition-all bg-white"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-11 h-11 flex items-center justify-center bg-primary/[0.06] text-primary rounded-xl flex-shrink-0 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                  <i className={`${r.icon} text-lg`} />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900 mb-1.5 group-hover:text-primary transition-colors">{r.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{r.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stats strip */}
        <div className="anim opacity-0 translate-y-6 transition-all duration-500 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { val: '15+', label: 'Yıllık Deneyim', icon: 'ri-award-line' },
            { val: '10.000+', label: 'Mutlu Müşteri', icon: 'ri-emotion-happy-line' },
            { val: '7/24', label: 'Acil Servis', icon: 'ri-time-line' },
            { val: '1 Yıl', label: 'Parça Garantisi', icon: 'ri-shield-check-line' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-2xl p-5 text-center border border-[#dbeafe]">
              <div className="w-10 h-10 flex items-center justify-center bg-primary/[0.06] text-primary rounded-xl mx-auto mb-3">
                <i className={`${s.icon} text-lg`} />
              </div>
              <p className="text-2xl font-extrabold text-slate-900 leading-none mb-1">{s.val}</p>
              <p className="text-xs text-slate-400 font-medium">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}